import { AssetsProvider } from '@/components/contexts/AssetsProvider'
import Assessment from '@/components/syllabus-tabs/Assessment'
import { Syllabus } from '@/kontent/content-types'
import {
	CommonContentTab,
	getCommonSyllabusData,
	getLayout,
	getStaticPaths,
} from '@/pages/learning-areas/[learningarea]/[syllabus]/[tab]'
import { CommonSyllabusPathParams } from '@/pages/learning-areas/[learningarea]/[syllabus]/[tab]/[stage]/[...afterStageSlugs]'
import { CommonPageProps, CustomSyllabusTab } from '@/types'
import { filterPreviewableSyllabusesOnly, redirectTo404 } from '@/utils'
import { cleanJson } from '@/utils/cleanJson'
import { getStaticPropsForRedirectTo404ForNonPreviewedSyllabus } from '@/utils/syllabus'
import { InferGetStaticPropsType } from 'next'
import { GetStaticProps } from 'next/types'

export const getStaticProps: GetStaticProps<
	CommonPageProps<
		Syllabus,
		CommonContentTab<Syllabus>,
		CommonSyllabusPathParams
	>,
	CommonSyllabusPathParams
> = async (context) => {
	const { params, preview } = context
	const {
		section,
		learningarea: learningareaSlug,
		syllabus: syllabusSlug,
	} = params

	const commonSyllabusData = await getCommonSyllabusData({
		...context,
		params: {
			...params,
			tab: 'assessment',
		},
	})

	// null when the syllabus could not be fetched from Kontent - degrade to a 404 instead
	// of dereferencing `syllabusResponse.item` below.
	if (!commonSyllabusData) {
		return redirectTo404(
			`/learning-areas/${[
				learningareaSlug,
				syllabusSlug,
				'assessment',
				section,
			].join('/')}`,
		)
	}

	const {
		config,
		mappings,
		syllabusesResponse,
		syllabusResponse,
		stages,
		years,
		stageGroups,
		keyLearningAreas,
		assets,
		webLinkExternals,
		webLinkVideos,
		defaultFocusAreaUrls,
		disabledStages,
		websiteConfiguration,
	} = commonSyllabusData

	// redirect to 404 if the syllabus is not allowed to be (pre)viewed
	const redirectPropsIfSyllabusIsNotPublished =
		getStaticPropsForRedirectTo404ForNonPreviewedSyllabus(
			syllabusResponse.item,
			`/404?path=/learning-areas/${[
				learningareaSlug,
				syllabusSlug,
				'overview',
				section,
			].join('/')}`,
		)
	if (redirectPropsIfSyllabusIsNotPublished) {
		return redirectPropsIfSyllabusIsNotPublished
	}

	// Check if URL KLA matches canonical KLA
	// Redirect to homepage if using non-canonical KLA
	const syllabus = syllabusResponse.item
	const canonicalKlaCodename =
		syllabus.elements.key_learning_area_default?.value?.[0]?.codename ||
		syllabus.elements.key_learning_area__items?.value?.[0]?.codename

	const urlKlaCodename = learningareaSlug.replace(/-/g, '_')

	if (canonicalKlaCodename && urlKlaCodename !== canonicalKlaCodename) {
		return redirectTo404(
			`/learning-areas/${[
				learningareaSlug,
				syllabusSlug,
				'assessment',
				section,
			].join('/')}`,
		)
	}

	const _props: InferGetStaticPropsType<typeof getStaticProps> = {
		mappings,
		preview: !!preview,
		previewData: null,
		params: {
			...params,
			slug: [
				'learning-areas',
				params.learningarea,
				params.syllabus,
				'assessment',
				section as unknown as string,
			],

			tab: 'assessment',
		},
		data: {
			config,
			pageResponse: null,
			syllabuses: {
				...syllabusesResponse,
				items: syllabusesResponse.items.filter(
					filterPreviewableSyllabusesOnly,
				),
			},
			syllabus: syllabusResponse,
			focusArea: null,
			stages,
			years,
			stageGroups,
			keyLearningAreas,
			glossaries: null,
			assets: (
				[
					'overview',
					'teaching-and-learning',
					'content',
					'assessment',
				] as CustomSyllabusTab[]
			).includes('assessment' as any)
				? assets
				: [],
			webLinkExternals,
			webLinkVideos,
			defaultFocusAreaUrls,
			disabledStages,
			websiteConfiguration,
		},
	}

	return {
		props: {
			rootLayoutClassName: 'max-w-none mx-0 px-0 !pt-0',
			...cleanJson(_props),
		},
	}
}

export default function Section(props) {
	return (
		<AssetsProvider assets={props.data.assets}>
			<Assessment {...props}></Assessment>
		</AssetsProvider>
	)
}

Section.getLayout = getLayout

export { getStaticPaths }
